import { Button, Col, Container, Grid, NetworkIcon, Typography } from 'gu-ui-kit'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import ActionLink from 'modules/Services/Account/subcomponents/ActionLink'
import TransactionList from './subcomponents/TransactionsList'
import FilterPanel from 'components/Filters/FilterPanel'
import EditableField from 'components/EditableField'
import { StatusBadge } from '../Network/subcomponents/AccountsList/subcomponents'
import difference from 'lodash/difference'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import {
  FETCH_NETWORK_ACCOUNT,
  GET_ACCOUNT_EXTRA_DATA,
  RESET_ACCOUNT_DATA,
  SET_ACCOUNT_DATA,
  SET_ACCOUNT_TRANSFERS_FILTERS,
  UPDATE_ACCOUNT_NAME,
} from 'modules/Services/types'
import config from 'modules/Services/Account/filters'
import { ROUTES } from 'constants/routes'
import { createPath } from 'history'
import clsx from 'clsx'
import Breadcrumbs from 'components/Breadcrumbs'
import useNetworkPalette from 'utils/hooks/useNetworkPalette'
import styles from 'modules/Services/Account/network-account.module.scss'
import { Placeholder } from 'modules/Services/Account/subcomponents'
import { notificationShow } from 'modules/Notifications/actions'
import HighRiskLabel from 'modules/Services/Network/subcomponents/HighRiskLabel'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import LeftSideActionButtons from 'modules/Services/Account/subcomponents/LeftSideActionButtons'

const STATUS_LIST = ['new', 'pending', 'verified']

const Account = () => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    params: { networkType, accountId },
  } = useRouteMatch()
  const { t } = useTranslation(['services'])
  const { cancelableDispatch } = useCancelToken()
  const breakpoints = useBreakpoint()

  const [accountTransfersConfig, setAccountTransfersConfig] =
    useLocalStorage('accountTransfersConfig')

  const {
    networkAccount: {
      office_url,
      login,
      status,
      auto_replenishment,
      balance,
      main_action,
      extra_actions = [],
      type,
      name,
      loading,
      loaded,
      failed,
      extra_info,
      extra_data,
    },
    i18n,
  } = useSelector((state) => ({
    networkAccount: state['services/network-account'],
    i18n: state['app'].i18n,
  }))

  const { networkId } = useNetworksData(networkType)
  const paletteClass = useNetworkPalette(networkId)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['services/network-account'].filters),
    config: config(),
    setAction: SET_ACCOUNT_TRANSFERS_FILTERS,
    additionalFilterState: accountTransfersConfig?.[networkType],
    localStorageKey: 'services/network-account',
  })

  useEffect(() => {
    if (!!type && type !== networkType) {
      history.replace(
        createPath({
          pathname: pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
            networkType: type,
            accountId,
          }),
          search: global.location.search,
        }),
      )
    }
  }, [type])

  const actions = useMemo(() => {
    const actions = [...extra_actions]
    const refundIndex = actions.indexOf('refund')

    if (['yd', 'yandex-business'].includes(networkType)) {
      const accessIndex = actions.indexOf('access')
      if (accessIndex > -1) {
        actions.splice(accessIndex, 1)
        actions.push('access_new')
      }
    }

    if (refundIndex === -1) {
      return actions
    }
    const refund = actions.splice(refundIndex, 1)[0]
    return [...actions, refund]
  }, [extra_actions, networkType])

  useEffect(() => {
    return () => {
      dispatch({
        type: RESET_ACCOUNT_DATA,
      })
    }
  }, [])

  useEffect(
    () =>
      new Promise((resolve, reject) =>
        cancelableDispatch({
          type: FETCH_NETWORK_ACCOUNT,
          payload: { accountId },
          meta: { resolve, reject },
        }),
      ).catch((e) => {
        if (e.status !== 499) {
          history.push(
            pathGen(ROUTES.SERVICES_NETWORK, {
              networkType,
            }),
          )
        }
      }),
    [accountId],
  )

  useEffect(
    () =>
      setAccountTransfersConfig({
        ...accountTransfersConfig,
        [networkType]: { ...filterManager.filters },
      }),
    [filterManager.filters, networkType],
  )

  const isActive = useMemo(() => STATUS_LIST.includes(status), [status])

  const rightSideActions = useMemo(
    () =>
      difference(actions, [
        main_action,
        'history',
        'delete',
        'archive',
        'refresh',
        'access',
        'access_new',
        'campaigns',
        'campaign-create',
      ]),
    [main_action, actions],
  )

  const applyAccountName = useCallback(
    (id, newValue) =>
      new Promise((resolve, reject) => {
        dispatch({
          type: UPDATE_ACCOUNT_NAME,
          payload: { id, name: newValue },
          meta: { resolve, reject },
        })
      }).then(() => {
        dispatch({
          type: SET_ACCOUNT_DATA,
          payload: { name: newValue },
        })

        dispatch(
          notificationShow({
            type: 'success',
            message: 'common::notifications.data_success_stored',
          }),
        )
      }),
    [],
  )

  const logoLinkProps = useMemo(() => {
    return office_url?.length > 0 && isActive
      ? { component: 'a', target: '_blank', href: office_url }
      : {
          component: Link,
          to: pathGen(ROUTES.SERVICES_NETWORK, {
            networkType,
          }),
        }
  }, [office_url, isActive, networkType])

  const breadcrumbsConfig = useMemo(
    () => [
      {
        title: t('services::heading'),
        to: pathGen(ROUTES.SERVICES),
      },
      {
        title: `${t(`services::networks.${networkType}`)}${
          networkType.match(/high-risk$/) ? ' (High Risk)' : ''
        }`,
        to: pathGen(ROUTES.SERVICES_NETWORK, {
          networkType: networkType,
        }),
      },
    ],
    [networkType, name, login, t],
  )

  useEffect(() => {
    if (['yd', 'yandex-business'].includes(networkType)) {
      networkId &&
        dispatch({
          type: GET_ACCOUNT_EXTRA_DATA,
          payload: {
            networkId,
            accountId,
          },
        })
    }
  }, [networkId])

  return (
    <div>
      <Helmet defer={false}>
        <title>
          {t(`services::networks.${networkType}`)}
          {login ? ' —  ' + login : ''}
        </title>
      </Helmet>
      <Breadcrumbs links={breadcrumbsConfig} />
      {loading ? (
        <Placeholder />
      ) : loaded && !failed ? (
        <Grid
          direction={breakpoints.fbs ? 'column' : 'row'}
          gap={breakpoints.fbs ? 0 : 16}
          className={styles['network-account__header']}
          wrap={false}
        >
          <Col className={styles['network-account__header-col']}>
            <Grid
              direction={'column'}
              gap={16}
            >
              <Grid
                direction={'column'}
                gap={16}
              >
                <Grid
                  alignItems={'center'}
                  direction={'row'}
                  justifyContent={'between'}
                  className={styles['network-account__logo-wrapper']}
                >
                  <Col>
                    <Button
                      variant={'ghost'}
                      link
                      className={styles['network-account__logo-wrapper-link']}
                      {...logoLinkProps}
                    >
                      <Grid
                        gap={8}
                        direction={'row'}
                        fluid={false}
                        alignItems={'center'}
                        vAlign={'top'}
                      >
                        <NetworkIcon
                          size={'s'}
                          name={networkType}
                          dark={paletteClass !== 'light'}
                          rounded
                        />
                        <Typography
                          variant={'Body-15'}
                          color={'Interface-Seventh'}
                          component={'div'}
                        >
                          {t(`services::networks.${networkType}`)}
                        </Typography>
                        {networkType.match(/high-risk$/) && <HighRiskLabel size={'s'} />}
                      </Grid>
                    </Button>
                  </Col>
                </Grid>

                <Grid
                  direction={'column'}
                  gap={8}
                  className={styles['network-account__account-description']}
                >
                  <Grid
                    direction={'row'}
                    gap={breakpoints.md ? 8 : 24}
                    alignItems={'center'}
                  >
                    <Typography
                      color={'Label-Fifth'}
                      variant={'Body-Accent-40'}
                      component={'div'}
                      noMargin
                      style={{ maxWidth: '100%' }}
                    >
                      {login && (
                        <EditableField
                          className={styles['network-account__edit-account-name']}
                          id={accountId}
                          name={name}
                          login={login}
                          apply={applyAccountName}
                        >
                          {name ?? login}
                        </EditableField>
                      )}
                    </Typography>
                    {!breakpoints.lg && status !== 'verified' && (
                      <StatusBadge
                        status={status}
                        size={'xs'}
                        variant={'Body-4'}
                      />
                    )}
                  </Grid>

                  <Grid gap={8}>
                    {login?.length > 0 && (
                      <Typography
                        className={styles['network-account__badge']}
                        component={'div'}
                        color={'Label-Secondary'}
                        variant={'Body-10'}
                        noMargin
                      >
                        {t('services::login')}
                        {':\u00A0'}
                        <Typography
                          component={'span'}
                          color={'Label-Fifth'}
                          variant={'inherit'}
                        >
                          {login}
                        </Typography>
                      </Typography>
                    )}

                    {breakpoints.lg && status !== 'verified' && (
                      <StatusBadge
                        status={status}
                        size={'xs'}
                        variant={'Body-4'}
                      />
                    )}

                    {extra_data && (
                      <>
                        {extra_data?.passport_organization_name && (
                          <Typography
                            className={styles['network-account__badge']}
                            component={'div'}
                            color={'Label-Secondary'}
                            variant={'Body-10'}
                            noMargin
                          >
                            {t('services::account.extra_data.passport_organization_name')}
                            {':\u00A0'}
                            <Typography
                              color={'Label-Fifth'}
                              variant={'inherit'}
                            >
                              {extra_data?.passport_organization_name}
                            </Typography>
                          </Typography>
                        )}

                        {extra_data?.yandex_user?.login && (
                          <Typography
                            className={styles['network-account__badge']}
                            component={'div'}
                            color={'Label-Secondary'}
                            variant={'Body-10'}
                            noMargin
                          >
                            {t('services::account.extra_data.yandex_user.login')}
                            {':\u00A0'}
                            <Typography
                              color={'Label-Fifth'}
                              variant={'inherit'}
                            >
                              {extra_data?.yandex_user.login}
                            </Typography>
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <LeftSideActionButtons
                actions={actions}
                accountId={accountId}
                networkType={networkType}
              />
            </Grid>
          </Col>

          <Col
            className={clsx(
              styles['network-account__header-col'],
              styles['network-account__header-col_description'],
            )}
          >
            <Grid
              direction={'column'}
              justifyContent={'space-between'}
              gap={breakpoints.fbs ? 16 : 'unset'}
            >
              {breakpoints.fbs ? null : (
                <Typography
                  variant="Body-15"
                  component={'div'}
                >
                  {t('services::balance')}
                </Typography>
              )}

              <Typography
                variant={'Body-Accent-60'}
                component={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatMoney(balance.amount, balance.currency),
                    }}
                  />
                }
              />

              <Grid
                wrap={false}
                alignItems={'center'}
                gap={16}
                flexJustify={'flex-start'}
              >
                {!!main_action && (
                  <Grid
                    flexGrow={breakpoints.fbs ? 1 : 0}
                    fluid={breakpoints.fbs ? true : false}
                  >
                    <ActionLink
                      action={main_action}
                      i18n={i18n}
                      id={accountId}
                      type={networkType}
                      balance={balance}
                    />
                  </Grid>
                )}

                {rightSideActions.map((action, i) => (
                  <ActionLink
                    key={'action-link-' + i}
                    action={action}
                    i18n={i18n}
                    id={accountId}
                    type={networkType}
                    balance={balance}
                    extra_info={extra_info}
                    auto_replenishment={auto_replenishment}
                    noLabel={breakpoints.l}
                  />
                ))}
              </Grid>
            </Grid>
          </Col>
        </Grid>
      ) : null}

      <Container className={styles['network-account__transfers-container']}>
        <FilterPanel
          filterManager={filterManager}
          className={styles['network-account__filter-panel']}
        />
        <TransactionList
          isDepositButton={main_action === 'deposit'}
          networkId={networkId}
          accountId={accountId}
          networkType={networkType}
        />
      </Container>
    </div>
  )
}

export default Account
